import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import BarcodeScanner from 'utils/BarcodeScanner';

/**
 * Custom hook for barcode scanner integration
 * Listens for barcode scans, finds products by UPC, and adds them to cart
 *
 * @param {Array} availableProducts - Array of available products to search
 * @param {Object} options - Configuration options
 * @param {string} options.revenueCenter - Revenue center for the product
 * @param {string} options.menuName - Menu name for the product
 * @param {boolean} options.enabled - Whether scanner is enabled (default: true)
 * @param {number} options.threshold - ms between keystrokes (default: 150)
 * @param {number} options.minLength - minimum barcode length (default: 4)
 * @param {number} options.submitDelay - ms to wait after last keystroke before auto-submit (default: 200)
 */
const useBarcodeScanner = (availableProducts, options = {}) => {
  const dispatch = useDispatch();
  const { cartItems } = useSelector((state) => state.cart);
  const scannerRef = useRef(null);
  const {
    revenueCenter = '',
    menuName = 'none',
    enabled = true,
    threshold = 150,  // increased for gun scanner compatibility
    minLength = 4,
    submitDelay = 200, // ms to wait after last keystroke
  } = options;

  // Store latest values in refs to avoid stale closures
  const cartItemsRef = useRef(cartItems);
  const productsRef = useRef(availableProducts);
  const optionsRef = useRef({ revenueCenter, menuName });

  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  useEffect(() => {
    productsRef.current = availableProducts;
  }, [availableProducts]);

  useEffect(() => {
    optionsRef.current = { revenueCenter, menuName };
  }, [revenueCenter, menuName]);

  const addToCart = useCallback((product) => {
    const currentCartItems = cartItemsRef.current;
    const { revenueCenter: rc, menuName: mn } = optionsRef.current;

    // Check if product already exists in cart
    const existingProduct = currentCartItems.find(
      (item) => item.ItemID === product.ItemID &&
      (!item.modifiers || item.modifiers.length === 0)
    );

    if (existingProduct) {
      // Increment quantity
      const newCartItems = currentCartItems.map((item) => {
        if (item.ItemID === product.ItemID &&
            (!item.modifiers || item.modifiers.length === 0)) {
          return {
            ...item,
            quantity: item.quantity + 1,
          };
        }
        return item;
      });

      dispatch({
        type: 'SET_CART',
        payload: newCartItems,
      });

      toast.success(
        `${product.ItemName || product.ItemDescription} quantity updated (${existingProduct.quantity + 1})`,
        { autoClose: 3500 }
      );
    } else {
      // Add new item to cart
      // Convert DefaultPrice from cents to dollars (API returns cents as string like "550.00")
      const priceInDollars = parseFloat(product.DefaultPrice) / 100;

      const newProduct = {
        ...product,
        DefaultPrice: priceInDollars,
        quantity: 1,
        modifiers: [],
        comments: '',
        expiration: Date.now() + 86400000, // 24 hours
        revenueCenter: rc,
        menuName: mn,
      };

      dispatch({
        type: 'SET_CART',
        payload: [...currentCartItems, newProduct],
      });

      toast.success(
        `${product.ItemName || product.ItemDescription} added to cart`,
        { autoClose: 3500 }
      );
    }
  }, [dispatch]);

  const handleScan = useCallback((barcode) => {
    const products = productsRef.current;

    if (!products || products.length === 0) {
      toast.warning('Products not loaded yet. Please try again.', { autoClose: 3500 });
      return;
    }

    // Clean barcode - remove any whitespace
    const cleanBarcode = barcode.trim();

    // Find product by UPC (case-insensitive)
    const foundProduct = products.find(
      (product) => product.UPC &&
        product.UPC.toLowerCase() === cleanBarcode.toLowerCase()
    );

    if (foundProduct) {
      // Debug: log full product data to verify price format
      console.log('=== BARCODE SCAN DEBUG ===');
      console.log('Barcode scanned:', cleanBarcode);
      console.log('Product found:', foundProduct.ItemName);
      console.log('DefaultPrice:', foundProduct.DefaultPrice, 'Type:', typeof foundProduct.DefaultPrice);
      console.log('ActualPrice:', foundProduct.ActualPrice);
      console.log('UPC:', foundProduct.UPC);
      console.log('Full product:', JSON.stringify(foundProduct, null, 2));
      console.log('=========================');

      // Check if product has required modifiers
      if (foundProduct.mods && foundProduct.mods.some(mod => mod.ModifierMinSelect > 0)) {
        toast.info(
          `${foundProduct.ItemName || foundProduct.ItemDescription} requires customization. Please select from menu.`,
          { autoClose: 4000 }
        );
        return;
      }

      addToCart(foundProduct);
    } else {
      toast.error(`Product not found for barcode: ${cleanBarcode}`, { autoClose: 3500 });
      console.warn('Barcode not found:', cleanBarcode);
    }
  }, [addToCart]);

  useEffect(() => {
    if (enabled) {
      scannerRef.current = new BarcodeScanner(handleScan, {
        threshold,
        minLength,
        submitDelay,
      });
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
    };
  }, [enabled, handleScan, threshold, minLength, submitDelay]);

  // Return control functions
  return {
    enable: () => scannerRef.current?.enable(),
    disable: () => scannerRef.current?.disable(),
    scanner: scannerRef.current,
  };
};

export default useBarcodeScanner;
